import { useState } from "react";
import { X, ArrowRight, AlertTriangle, Info, ShieldCheck, ClipboardPaste } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { BIOMETRIC } from "@/constants/testIds";

/**
 * Biometric raw-file PREVIEW modal. Ingests the KALMARPEI daily-transaction
 * export, groups by emp_code, applies the calculation pipeline, and surfaces:
 *   - inferred date on every row (positional inference — load-bearing)
 *   - per-day working breakdown + bucketed hours
 *   - the exception queue (missing punches / unmapped codes / absent staff)
 *   - row-count / PPE-mismatch errors that block the import
 *
 * This is PREVIEW ONLY this iteration — apply-to-pay-lines waits on rostered
 * times, rounding, and break rules that Ben hasn't confirmed yet. When those
 * land it's a settings change, not a rebuild.
 */

const EXCEPTION_LABEL = {
    no_clock_out: "In without out",
    no_clock_in: "Out without in",
    implausible_span: "Implausible span",
    no_punches_all_period: "No punches for the whole period",
    employee_absent_from_file: "Missing from the biometric file",
};


function ErrorBanner({ items, kind, icon: Icon }) {
    if (!items || items.length === 0) return null;
    const bg = kind === "error" ? "bg-red-50 border-red-300 text-red-900"
             : kind === "warn"  ? "bg-amber-50 border-amber-300 text-amber-900"
             : "bg-blue-50 border-blue-300 text-blue-900";
    return (
        <div className={`border rounded-[3px] p-3 mb-3 ${bg}`}>
            <div className="flex items-start gap-2">
                <Icon size={14} className="mt-0.5 shrink-0" />
                <ul className="text-[12px] space-y-1">
                    {items.map((it, i) => (
                        <li key={i}>
                            {typeof it === "string" ? it : JSON.stringify(it)}
                        </li>
                    ))}
                </ul>
            </div>
        </div>
    );
}


export default function BiometricPreviewModal({ open, onClose, periodId, onApplied }) {
    const [text, setText] = useState("");
    const [preview, setPreview] = useState(null);
    const [busy, setBusy] = useState(false);
    const [expanded, setExpanded] = useState({}); // { employee_id: bool }
    const [ackOpen, setAckOpen] = useState(false);
    const [ack, setAck] = useState(false);
    const [overwriteAck, setOverwriteAck] = useState(false);
    const [settings, setSettings] = useState(null);
    // Round 13 · Group C. Row-count deltas warn-and-ack. State must
    // live above the early-return so React's hooks order stays stable
    // — moving it inside the block below the `if (!open) return null`
    // guard is a rules-of-hooks violation the lint catches.
    const [rowCountAck, setRowCountAck] = useState(false);

    if (!open) return null;

    const close = () => { setText(""); setPreview(null); setExpanded({}); setAckOpen(false); setAck(false); setOverwriteAck(false); setRowCountAck(false); onClose?.(); };

    const runPreview = async () => {
        if (!text.trim() || !periodId) return;
        setBusy(true);
        try {
            const [{ data }, { data: s }] = await Promise.all([
                api.post(`/pay-periods/${periodId}/biometric/preview`, { text }),
                api.get("/payroll/settings"),
            ]);
            setPreview(data);
            setSettings(s);
            const parseIssues = (data.parse_errors || []).length + (data.row_count_errors || []).length;
            if (parseIssues > 0) toast.error(`${parseIssues} blocking issue(s) — see banners`);
            else if ((data.exceptions || []).length > 0) toast.warning(`${data.exceptions.length} exception(s) queued`);
            else toast.success("Parsed cleanly. Ready to apply.");
        } catch (ex) {
            toast.error(formatApiError(ex?.response?.data?.detail) || "Preview failed");
        } finally {
            setBusy(false);
        }
    };

    // Rules Michael must acknowledge — mirror backend _UNCONFIRMED_MESSAGES
    const unconfirmedRules = (() => {
        if (!settings) return [];
        const map = {
            rostered_clamp_enabled: "Paying from clock-on time — no rostered start clamp is applied.",
            rounding_increment_minutes: "No time rounding is being applied.",
            unpaid_break_minutes: "No unpaid break is being deducted from any day.",
        };
        return Object.entries(map)
            .filter(([k]) => settings[k] && !settings[k].confirmed)
            .map(([k, msg]) => ({ key: k, message: msg }));
    })();

    const canApply = preview
        && (preview.parse_errors || []).length === 0
        && !preview.ppe_mismatch
        && (preview.exceptions || []).filter(e => e.kind !== "no_punches_all_period" && e.kind !== "employee_absent_from_file").length === 0;

    const rowCountDeltas = preview?.row_count_errors || [];
    const unmapped = preview?.unmapped_codes || [];
    const hasDeltas = rowCountDeltas.length > 0;

    const applyNow = async () => {
        try {
            const { data } = await api.post(`/pay-periods/${periodId}/biometric/apply`, {
                text,
                exception_resolutions: [],
                acknowledged_unconfirmed_rules: true,
                acknowledged_row_count_deltas: rowCountAck || !hasDeltas,
                overwrite_manual_edits: overwriteAck,
            });
            const skipped = (data.unmapped_skipped || []).length;
            const deltas = (data.row_count_deltas || []).length;
            const parts = [`Applied to ${data.applied_count} employees`];
            if (skipped) parts.push(`${skipped} unmatched skipped`);
            if (deltas) parts.push(`${deltas} row-count delta(s)`);
            toast.success(parts.join(" · "));
            onApplied?.();
            close();
        } catch (ex) {
            const d = ex?.response?.data?.detail;
            if (d && d.code === "manual_edits_present") {
                toast.error(`${d.manually_edited.length} line(s) have manual edits — tick the overwrite box`);
                return;
            }
            if (d && d.code === "row_count_deltas_ack_required") {
                toast.error("Row-count deltas found — tick the acknowledgement and try again.");
                return;
            }
            toast.error(formatApiError(d) || "Apply failed");
        }
    };

    return (
        <div className="fixed inset-0 z-50 flex items-start justify-center pt-6" role="dialog" aria-modal="true">
            <div className="absolute inset-0 bg-black/50" onClick={close} />
            <div className="relative bg-white border border-neutral-200 shadow-2xl w-full max-w-[1100px] max-h-[92vh] flex flex-col rounded-[3px]">
                <div className="px-6 py-5 border-b border-neutral-200 flex items-start justify-between">
                    <div>
                        <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-display">
                            Biometric raw ingest · preview & apply
                        </div>
                        <div className="font-display font-bold text-2xl text-[color:var(--kp-charcoal)] mt-0.5">
                            From KALMARPEI daily-transaction file
                        </div>
                        <div className="text-[11px] text-neutral-500 mt-1 max-w-[720px]">
                            Rows are matched by <strong>emp code</strong>, dates are inferred by position.
                            Apply writes buckets onto the draft lines; you can still edit them by hand afterwards.
                        </div>
                    </div>
                    <button onClick={close} className="p-1 text-neutral-500 hover:text-black">
                        <X size={16} />
                    </button>
                </div>

                <div className="px-6 py-4 border-b border-neutral-200">
                    <div className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display mb-1 flex items-center gap-2">
                        <ClipboardPaste size={12} /> Paste the raw file
                    </div>
                    <textarea
                        data-testid={BIOMETRIC.textarea}
                        rows={5}
                        value={text}
                        onChange={(e) => setText(e.target.value)}
                        placeholder="KALMARPEI PAYROLL SYSTEM LAE PNG 2026…"
                        className="kp-focus w-full border border-neutral-300 px-3 py-2 text-[12px] font-mono-money bg-white rounded-[3px]"
                        style={{ whiteSpace: "pre" }}
                    />
                    <div className="mt-3 flex justify-end gap-2">
                        <button
                            data-testid={BIOMETRIC.previewBtn}
                            onClick={runPreview}
                            disabled={!text.trim() || busy}
                            className="inline-flex items-center gap-2 px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] border border-neutral-300 text-neutral-700 rounded-[3px] disabled:opacity-40"
                        >
                            {busy ? "Parsing…" : "Preview"} <ArrowRight size={14} />
                        </button>
                        <button
                            data-testid={BIOMETRIC.applyBtn}
                            onClick={() => setAckOpen(true)}
                            disabled={!canApply || busy}
                            className="inline-flex items-center gap-2 px-5 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] disabled:opacity-40"
                            style={{ background: "var(--kp-red)" }}
                        >
                            Apply to pay run <ArrowRight size={14} />
                        </button>
                    </div>
                </div>

                <div className="flex-1 overflow-y-auto px-6 py-5" data-testid={BIOMETRIC.previewResult}>
                    {!preview && (
                        <div className="text-[13px] text-neutral-400 italic text-center py-16">
                            Waiting for a paste…
                        </div>
                    )}
                    {preview && (
                        <>
                            <ErrorBanner
                                kind="error"
                                icon={AlertTriangle}
                                items={[
                                    ...(preview.parse_errors || []),
                                    ...(preview.ppe_mismatch ? [preview.ppe_mismatch] : []),
                                ]}
                            />
                            <ErrorBanner kind="warn" icon={AlertTriangle} items={preview.data_warnings} />

                            {/* Round 13 · Group C — row-count deltas surface as a warn-and-ack banner, not a refusal. */}
                            {hasDeltas && (
                                <div
                                    data-testid={BIOMETRIC.rowCountDeltaBanner}
                                    className="border border-amber-300 bg-amber-50 rounded-[3px] p-3 mb-3"
                                >
                                    <div className="flex items-start gap-2">
                                        <AlertTriangle size={14} className="mt-0.5 shrink-0 text-amber-700" />
                                        <div className="flex-1 text-[12px] text-amber-900">
                                            <div className="font-display font-semibold uppercase tracking-[0.14em] text-[11px] mb-1">
                                                Row-count deltas — file has employees with different row counts
                                            </div>
                                            <ul className="list-disc pl-5 space-y-0.5">
                                                {rowCountDeltas.map((d, i) => (
                                                    <li key={i}>
                                                        <span className="font-mono-money">{d.emp_code}</span>: {d.actual} rows (expected {d.expected})
                                                    </li>
                                                ))}
                                            </ul>
                                            <label className="mt-2 flex items-center gap-2 cursor-pointer">
                                                <input
                                                    data-testid={BIOMETRIC.rowCountDeltaAck}
                                                    type="checkbox"
                                                    checked={rowCountAck}
                                                    onChange={(e) => setRowCountAck(e.target.checked)}
                                                    className="accent-[color:var(--kp-red)]"
                                                />
                                                <span>I've reviewed the deltas — apply anyway.</span>
                                            </label>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <div className="grid grid-cols-4 gap-3 mb-4 text-[12px]">
                                <SummaryChip label="PPE" value={preview.ppe_date} />
                                <SummaryChip label="Rows / employee" value={preview.rowcount} />
                                <SummaryChip label="Employees in file" value={preview.employees_in_file} />
                                <SummaryChip
                                    label="Date source"
                                    value={preview.date_source === "column" ? "date column" : "positional"}
                                    testid={BIOMETRIC.dateSourceChip}
                                />
                            </div>

                            {/* Round 13 · Group C — unmatched employees get their own header row so name+code sit together in one place. */}
                            {unmapped.length > 0 && (
                                <div
                                    data-testid={BIOMETRIC.unmatchedHeader}
                                    className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display mb-2 mt-1"
                                >
                                    Unmatched · {unmapped.length} employee{unmapped.length === 1 ? "" : "s"} in file with no master record — will be skipped, not paid
                                </div>
                            )}

                            <ExceptionsPanel
                                exceptions={preview.exceptions || []}
                                unmapped={preview.unmapped_codes || []}
                                absent={preview.absent_employees || []}
                            />

                            <div className="mt-5">
                                <div className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display mb-2">
                                    Mapped employees · {(preview.mapped || []).length}
                                </div>
                                {(preview.mapped || []).map((m) => (
                                    <EmployeeBlock
                                        key={m.employee_id}
                                        block={m}
                                        expanded={!!expanded[m.employee_id]}
                                        toggle={() => setExpanded((s) => ({ ...s, [m.employee_id]: !s[m.employee_id] }))}
                                    />
                                ))}
                            </div>
                        </>
                    )}
                </div>
            </div>

            {ackOpen && (
                <div data-testid={BIOMETRIC.ackModal} className="fixed inset-0 z-[60] flex items-center justify-center">
                    <div className="absolute inset-0 bg-black/70" onClick={() => setAckOpen(false)} />
                    <div className="relative bg-white border border-neutral-200 shadow-2xl w-full max-w-[560px] p-6 rounded-[3px]">
                        <div className="text-[10px] uppercase tracking-[0.24em] text-neutral-500 font-display mb-1">Acknowledge and apply</div>
                        <h2 className="font-display font-bold text-xl text-[color:var(--kp-charcoal)] mb-3">
                            You are about to write hours onto the draft pay run
                        </h2>
                        {unconfirmedRules.length > 0 ? (
                            <div className="mb-4">
                                <div className="text-[12px] text-neutral-700 mb-2">
                                    The following rules are still <strong>awaiting client answers</strong>. Applying now means:
                                </div>
                                <ul className="border border-amber-300 bg-amber-50 rounded-[3px] p-3 text-[12px] text-amber-900 list-disc pl-5 space-y-1">
                                    {unconfirmedRules.map((r) => <li key={r.key}>{r.message}</li>)}
                                </ul>
                            </div>
                        ) : (
                            <div className="mb-4 text-[12px] text-neutral-600">All calculation rules are confirmed. Nothing is being assumed.</div>
                        )}

                        <label className="flex items-start gap-2 text-[12px] cursor-pointer mb-2">
                            <input
                                data-testid={BIOMETRIC.ackCheckbox}
                                type="checkbox" checked={ack} onChange={(e) => setAck(e.target.checked)}
                                className="mt-0.5 accent-[color:var(--kp-red)]"
                            />
                            <span>I've read the assumptions and want to apply anyway.</span>
                        </label>

                        <label className="flex items-start gap-2 text-[12px] cursor-pointer mb-4">
                            <input
                                data-testid={BIOMETRIC.overwriteCheckbox}
                                type="checkbox" checked={overwriteAck} onChange={(e) => setOverwriteAck(e.target.checked)}
                                className="mt-0.5 accent-[color:var(--kp-red)]"
                            />
                            <span>Overwrite any manual edits already made to these lines (if re-applying).</span>
                        </label>

                        <div className="flex justify-end gap-2">
                            <button onClick={() => setAckOpen(false)}
                                    className="px-4 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-neutral-600 hover:text-black">
                                Cancel
                            </button>
                            <button
                                data-testid={BIOMETRIC.ackConfirmBtn}
                                onClick={applyNow}
                                disabled={!ack}
                                className="px-5 py-2 text-[12px] font-display font-semibold uppercase tracking-[0.12em] text-white rounded-[3px] disabled:opacity-40"
                                style={{ background: "var(--kp-red)" }}
                            >
                                Apply
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}


function SummaryChip({ label, value, testid }) {
    return (
        <div className="border border-neutral-200 rounded-[3px] p-2 bg-neutral-50">
            <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-display">{label}</div>
            <div data-testid={testid} className="font-mono-money text-[13px] text-[color:var(--kp-charcoal)] mt-0.5">{value ?? "—"}</div>
        </div>
    );
}


function ExceptionsPanel({ exceptions, unmapped, absent }) {
    const total = (exceptions?.length || 0) + (unmapped?.length || 0);
    if (total === 0 && (absent?.length || 0) === 0) {
        return (
            <div className="border border-green-200 bg-green-50 rounded-[3px] p-3 flex items-center gap-2 text-[13px] text-green-800">
                <ShieldCheck size={14} /> No exceptions.
            </div>
        );
    }
    return (
        <div data-testid={BIOMETRIC.exceptionsPanel}>
            <div className="text-[11px] uppercase tracking-[0.14em] text-neutral-500 font-display mb-2 flex items-center gap-2">
                <AlertTriangle size={12} className="text-amber-600" /> Exception queue · {exceptions.length + unmapped.length + absent.length}
            </div>
            <div className="border border-amber-300 bg-amber-50 rounded-[3px] divide-y divide-amber-200">
                {unmapped.map((u, i) => (
                    <div key={`u${i}`} data-testid={BIOMETRIC.unmappedRow(i)} className="p-3 text-[12px]">
                        <div className="font-display font-semibold uppercase tracking-[0.14em] text-[11px] text-amber-900">
                            Unknown emp code
                        </div>
                        <div className="mt-1">
                            <span className="font-mono-money">{u.emp_code}</span>
                            {" · "}{u.surname}, {u.firstname} ({u.position || "—"})
                            {" · "}{u.row_count} rows
                        </div>
                        {u.suggested_employee_id && (
                            <div className="mt-1 text-[11px] text-amber-800">
                                <Info size={10} className="inline" /> Suggested mapping is available — confirm in the Employees screen.
                            </div>
                        )}
                    </div>
                ))}
                {absent.map((a, i) => (
                    <div key={`a${i}`} data-testid={BIOMETRIC.absentRow(i)} className="p-3 text-[12px]">
                        <div className="font-display font-semibold uppercase tracking-[0.14em] text-[11px] text-amber-900">
                            Active employee missing from file
                        </div>
                        <div className="mt-1">
                            <strong>{a.employee_name}</strong> · code <span className="font-mono-money">{a.emp_code}</span>
                        </div>
                    </div>
                ))}
                {exceptions.map((e, i) => (
                    <div key={`e${i}`} data-testid={BIOMETRIC.exception(i)} className="p-3 text-[12px]">
                        <div className="font-display font-semibold uppercase tracking-[0.14em] text-[11px] text-amber-900">
                            {EXCEPTION_LABEL[e.kind] || e.kind}
                        </div>
                        <div className="mt-1">
                            {e.employee_name || "(unmapped)"}
                            {e.emp_code ? <> · <span className="font-mono-money">{e.emp_code}</span></> : null}
                            {e.inferred_date ? <> · {e.inferred_date} ({e.weekday})</> : null}
                        </div>
                        <div className="text-[11px] text-amber-800 mt-0.5">{e.reason}</div>
                        {(e.am_in || e.am_out || e.pm_out) && (
                            <div className="text-[11px] text-neutral-500 mt-0.5 font-mono-money">
                                raw: AM-in {e.am_in || "—"} · AM-out {e.am_out || "—"} · PM-out {e.pm_out || "—"}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        </div>
    );
}


function EmployeeBlock({ block, expanded, toggle }) {
    return (
        <div className="border border-neutral-200 rounded-[3px] mb-3 overflow-hidden">
            <button
                onClick={toggle}
                className="w-full px-4 py-3 flex items-center justify-between text-left hover:bg-neutral-50"
            >
                <div>
                    <div className="text-[13px] font-medium text-[color:var(--kp-charcoal)]">{block.employee_name}</div>
                    <div className="text-[10px] uppercase tracking-[0.14em] text-neutral-500 font-mono-money mt-0.5">
                        {block.emp_code}
                    </div>
                </div>
                <div className="text-[11px] font-mono-money text-neutral-700 flex gap-3">
                    <span>Reg <strong>{block.totals?.regular ?? 0}</strong></span>
                    <span>×1.5 <strong>{block.totals?.time_and_half ?? 0}</strong></span>
                    <span>×2.0 <strong>{block.totals?.double ?? 0}</strong></span>
                </div>
            </button>
            {expanded && (
                <div className="border-t border-neutral-100 overflow-x-auto">
                    <table className="w-full text-[11px]">
                        <thead>
                            <tr className="bg-neutral-50 text-left text-neutral-500 uppercase tracking-[0.14em] font-display">
                                <th className="px-3 py-2 font-semibold">#</th>
                                <th className="px-3 py-2 font-semibold">Date</th>
                                <th className="px-3 py-2 font-semibold">Day</th>
                                <th className="px-3 py-2 font-semibold">AM in</th>
                                <th className="px-3 py-2 font-semibold">AM out</th>
                                <th className="px-3 py-2 font-semibold">PM out</th>
                                <th className="px-3 py-2 font-semibold text-right">Hours</th>
                                <th className="px-3 py-2 font-semibold text-right">Reg</th>
                                <th className="px-3 py-2 font-semibold text-right">×1.5</th>
                                <th className="px-3 py-2 font-semibold text-right">×2.0</th>
                                <th className="px-3 py-2 font-semibold">Status</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(block.days || []).map((d, i) => (
                                <tr
                                    key={i}
                                    data-testid={BIOMETRIC.dayRow(block.employee_id, i)}
                                    className={`border-t border-neutral-100 ${d.status === "exception" ? "bg-red-50/40" : ""}`}
                                >
                                    <td className="px-3 py-1.5 font-mono-money">{d.row_index}</td>
                                    <td className="px-3 py-1.5 font-mono-money">{d.inferred_date}</td>
                                    <td className="px-3 py-1.5">{d.weekday}</td>
                                    <td className="px-3 py-1.5 font-mono-money">{d.am_in || "—"}</td>
                                    <td className="px-3 py-1.5 font-mono-money">{d.am_out || "—"}</td>
                                    <td className="px-3 py-1.5 font-mono-money">{d.pm_out || "—"}</td>
                                    <td className="px-3 py-1.5 text-right font-mono-money">{d.hours_worked ?? "—"}</td>
                                    <td className="px-3 py-1.5 text-right font-mono-money">{d.buckets?.regular ?? 0}</td>
                                    <td className="px-3 py-1.5 text-right font-mono-money">{d.buckets?.time_and_half ?? 0}</td>
                                    <td className="px-3 py-1.5 text-right font-mono-money">{d.buckets?.double ?? 0}</td>
                                    <td className="px-3 py-1.5">
                                        {d.status === "ok" && d.is_public_holiday && (
                                            <span className="text-[10px] uppercase tracking-[0.12em] text-[color:var(--kp-red)] font-display">Public holiday</span>
                                        )}
                                        {d.status === "exception" && <span className="text-[10px] uppercase tracking-[0.12em] text-red-700 font-display">{d.kind}</span>}
                                        {d.status === "blank" && <span className="text-[10px] uppercase tracking-[0.12em] text-neutral-400 font-display">day off</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}
